import { useQuery } from '@tanstack/react-query';
import { Bell, Check, Search as SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAME_SEARCH_ENABLED } from '../lib/features';
import { queryKeys } from '../lib/queryKeys';
import { cn } from '../lib/utils';
import { api } from '../services/api';
import { formatTime } from '../utils/dates';
import { CaseCard } from './cases/CaseCard';
import {
  CasesViewEmpty,
  CasesViewError,
  CasesViewPreparing,
  CasesViewSkeleton,
} from './cases/CasesStates';
import { buildCaseEntries, buildNovedades } from './cases/cases.model';

function CasesView() {
  const navigate = useNavigate();
  const [expandedCaso, setExpandedCaso] = useState<string | null>(null);
  const [expandedProc, setExpandedProc] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<'all' | 'name' | 'process'>('all');

  const {
    data: allData = {},
    isLoading: loadingData,
    isError: errorData,
    refetch: refetchData,
  } = useQuery({
    queryKey: queryKeys.historicalByName,
    queryFn: api.getHistoricalDataByName,
  });

  const {
    data: recentChanges = [],
    isError: errorChanges,
    refetch: refetchChanges,
  } = useQuery({
    queryKey: queryKeys.recentChanges(50),
    queryFn: () => api.getRecentChanges(50),
  });

  const {
    data: stats,
    isError: errorStats,
    refetch: refetchStats,
  } = useQuery({
    queryKey: queryKeys.statsByName,
    queryFn: api.getStatsByName,
  });

  const {
    data: searches = [],
    isLoading: loadingSearches,
    refetch: refetchSearches,
  } = useQuery({
    queryKey: queryKeys.searches,
    queryFn: api.getSearches,
  });

  const isLoading = loadingData || loadingSearches;
  const isError = errorData || errorChanges || errorStats;

  const handleRetry = () => {
    refetchData();
    refetchChanges();
    refetchStats();
    refetchSearches();
  };

  const allCases = useMemo(() => buildCaseEntries(allData), [allData]);
  const novedadesByCase = useMemo(
    () => buildNovedades(recentChanges, allCases),
    [recentChanges, allCases]
  );

  const totalCasos = allCases.length;
  const muestraBusqueda = totalCasos > 8;

  const filteredCases = useMemo(() => {
    if (!muestraBusqueda) return allCases;
    let result = allCases;
    if (filtro !== 'all') result = result.filter((c) => c.searchType === filtro);
    const q = busqueda.trim().toLowerCase();
    if (q) result = result.filter((c) => c.nombre.toLowerCase().includes(q));
    return result;
  }, [allCases, muestraBusqueda, filtro, busqueda]);

  const novedadesList = useMemo(
    () => [...novedadesByCase.values()].sort((a, b) => b.timestamp - a.timestamp),
    [novedadesByCase]
  );

  const casosConNovedad = novedadesByCase.size;
  const cambiosUltimas24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return recentChanges.filter((c) => new Date(c.timestamp).getTime() >= cutoff).length;
  }, [recentChanges]);

  const statItems = [
    { valor: totalCasos, etiqueta: 'Casos seguidos' },
    { valor: stats?.totalChanges ?? 0, etiqueta: 'Cambios en total' },
    { valor: cambiosUltimas24h, etiqueta: 'Últimas 24 horas' },
    { valor: casosConNovedad, etiqueta: 'Con novedades' },
  ];

  // El filtro «Nombres» solo se ofrece si hay casos de ese tipo: las búsquedas por
  // nombre ya no se pueden crear, pero las existentes se siguen mostrando.
  const hayCasosPorNombre = allCases.some((c) => c.searchType === 'name');

  const filtros: Array<{ value: 'all' | 'name' | 'process'; label: string }> = [
    { value: 'all', label: 'Todos' },
    ...(NAME_SEARCH_ENABLED || hayCasosPorNombre
      ? ([{ value: 'name', label: 'Nombres' }] as const)
      : []),
    { value: 'process', label: 'Radicados' },
  ];

  if (isLoading) return <CasesViewSkeleton />;
  if (isError) return <CasesViewError onRetry={handleRetry} />;
  if (totalCasos === 0) {
    return searches.length > 0 ? (
      <CasesViewPreparing count={searches.length} />
    ) : (
      <CasesViewEmpty />
    );
  }

  const sinResultados = muestraBusqueda && filteredCases.length === 0;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-end gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-serif text-[1.625rem] font-semibold tracking-tight text-ink m-0">
            Tus casos
          </h2>
          <p className="text-[0.875rem] text-ink-tertiary mt-1 mb-0">
            Última revisión: hoy, {formatTime(new Date())}
          </p>
        </div>
      </div>

      {/* Banner de novedades */}
      {novedadesList.length > 0 ? (
        <div className="bg-surface border border-accent/30 rounded-lg mb-4 overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5">
            <Bell size={15} className="text-accent" />
            <span className="text-[0.875rem] font-semibold text-accent tracking-wide">
              Novedades en tus casos
            </span>
            <span className="bg-accent-subtle text-accent text-[0.75rem] font-semibold px-2 py-px rounded-pill">
              {novedadesList.length}
            </span>
          </div>
          <div className="flex flex-col">
            {novedadesList.map((n) => (
              <button
                key={n.caseKey}
                onClick={() => setExpandedCaso(n.caseKey)}
                className="flex items-baseline gap-2.5 w-full text-left px-4 py-3 min-h-11 bg-transparent border-none border-t border-border cursor-pointer hover:bg-surface-raised transition-colors duration-150"
              >
                <span className="w-1.75 h-1.75 rounded-full bg-accent flex-shrink-0 -translate-y-0.5" />
                <span className="flex-1 min-w-0 text-[0.9375rem] text-ink leading-snug">
                  <strong className="font-semibold">{n.nombre}</strong> — {n.texto}
                </span>
                <span className="text-[0.8125rem] text-ink-tertiary flex-shrink-0 whitespace-nowrap">
                  {n.hace}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 bg-surface border border-border rounded-lg px-4 py-3.5 mb-4">
          <Check size={17} className="text-success" />
          <span className="text-[0.875rem] text-ink-secondary">
            Te avisaremos por correo cuando algo cambie.
          </span>
        </div>
      )}

      {/* Franja de stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-lg overflow-hidden mb-6">
        {statItems.map((s) => (
          <div key={s.etiqueta} className="bg-surface px-4 py-3">
            <div className="font-serif text-[1.375rem] font-bold text-accent leading-tight">
              {s.valor}
            </div>
            <div className="text-[0.75rem] text-ink-tertiary font-medium mt-0.5">{s.etiqueta}</div>
          </div>
        ))}
      </div>

      {/* Búsqueda + filtro */}
      {muestraBusqueda && (
        <div className="flex gap-2.5 flex-wrap items-center mb-4">
          <div className="flex-1 min-w-55 flex items-center gap-2 bg-surface-inset border border-border-emphasis rounded-md px-3">
            <SearchIcon size={15} className="text-ink-tertiary" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o radicado…"
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-ink text-[0.9375rem] py-3 min-h-11"
            />
          </div>
          <div className="inline-flex bg-surface-inset border border-border rounded-md p-0.5 gap-0.5">
            {filtros.map((f) => (
              <button
                key={f.value}
                onClick={() => setFiltro(f.value)}
                className={cn(
                  'px-3.5 py-2 min-h-9.5 border-none rounded-sm text-[0.84375rem] cursor-pointer transition-colors duration-150',
                  filtro === f.value
                    ? 'bg-surface-raised text-ink font-semibold'
                    : 'bg-transparent text-ink-muted font-normal'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {sinResultados && (
        <p className="text-center text-ink-tertiary text-[0.9375rem] py-8">
          No encontramos casos que coincidan con «{busqueda}».
        </p>
      )}

      {/* Lista de casos */}
      <div className="flex flex-col gap-3">
        {filteredCases.map((entry) => (
          <CaseCard
            key={entry.key}
            entry={entry}
            novedad={novedadesByCase.get(entry.key)}
            expanded={expandedCaso === entry.key}
            onToggle={() =>
              setExpandedCaso((prev) => {
                const next = prev === entry.key ? null : entry.key;
                if (next !== prev) setExpandedProc(null);
                return next;
              })
            }
            expandedProcKey={expandedProc}
            onToggleProc={(key) => setExpandedProc((prev) => (prev === key ? null : key))}
            onNavigateProcess={(idProceso) => navigate(`/detalle/proceso/${idProceso}`)}
          />
        ))}
      </div>
    </div>
  );
}

export default CasesView;
